import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Building2, MapPin, Stethoscope, Star, ShieldCheck, ArrowLeft, CalendarPlus,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as bookingsApi from '../../api/bookings.api';
import * as reviewsApi from '../../api/reviews.api';
import { errorMessage } from '../../api/client';

function ClinicDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get('orderId');

  const [clinic, setClinic] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [c, r] = await Promise.all([
          bookingsApi.getPublicClinic(id),
          reviewsApi.listClinicReviews(id).catch(() => []),
        ]);
        if (cancelled) return;
        setClinic(c);
        setReviews(r || []);
      } catch (e) {
        toast.error(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  async function selectForBooking() {
    if (!orderId) return;
    setBusy(true);
    try {
      await bookingsApi.assignClinic(orderId, id);
      toast.success('Clinic selected — pick a date next.');
      navigate(`/orders/${orderId}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingSpinner />;
  if (!clinic) return <PageHeader title="Clinic not found" />;

  const cp = clinic.clinicProfile || {};
  const perf = cp.performance || {};
  const addr = [cp.addressLine1, cp.addressLine2, cp.city, cp.state, cp.postcode].filter(Boolean).join(', ');
  const mapsLink = cp.location?.coordinates?.length === 2
    ? `https://maps.google.com/?q=${cp.location.coordinates[1]},${cp.location.coordinates[0]}`
    : null;

  return (
    <>
      <Link className="btn btn-link" to={orderId ? `/clinics?orderId=${orderId}` : '/clinics'}><ArrowLeft size={14} /> Back to clinics</Link>
      <PageHeader
        title={cp.clinicName || clinic.name}
        subtitle={addr || ''}
      />

      <div className="card">
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Building2 size={18} />
          <span className="badge success"><ShieldCheck size={12} /> MMC + APC verified</span>
          {cp.prescribingDoctor && (
            <span className="muted"><Stethoscope size={14} /> {cp.prescribingDoctor}</span>
          )}
          {mapsLink && (
            <a className="btn btn-link" href={mapsLink} target="_blank" rel="noreferrer">
              <MapPin size={14} /> Open in Maps
            </a>
          )}
        </div>

        <div className="grid grid-4" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-4)' }}>
          <Stat label="Avg loss"      value={perf.avgWeightLossKg ? `${perf.avgWeightLossKg.toFixed(1)} kg` : '—'} />
          <Stat label="Avg speed"     value={perf.avgSpeedDays ? `${perf.avgSpeedDays} d` : '—'} />
          <Stat label="Success rate"  value={perf.successRatePct ? `${perf.successRatePct}%` : '—'} />
          <Stat label="Rating" value={perf.customerRating ? <><Star size={14} /> {perf.customerRating.toFixed(1)}</> : '—'} sub={`${perf.totalCompleted || 0} completed`} />
        </div>

        {orderId && (
          <div style={{ marginTop: 'var(--s-4)' }}>
            <button className="btn btn-primary" disabled={busy} onClick={selectForBooking}>
              <CalendarPlus size={14} /> Select for booking
            </button>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <h3>User reviews</h3>
        {reviews.length === 0 ? (
          <p className="muted">No reviews yet.</p>
        ) : reviews.map((r) => (
          <div key={r._id} style={{ borderTop: '1px solid var(--ink-200)', padding: '12px 0' }}>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <Star size={14} /> <strong>{r.rating}/5</strong>
              <span className="muted text-sm">· {r.user?.name || 'User'}</span>
              <span className="muted text-sm">· {new Date(r.createdAt).toLocaleDateString()}</span>
            </div>
            {r.comment && <p style={{ marginTop: 6 }}>{r.comment}</p>}
            {r.clinicReply && (
              <div style={{ marginTop: 6, padding: 8, background: 'var(--ink-100)', borderRadius: 6 }}>
                <span className="muted text-xs">Clinic reply:</span> {r.clinicReply}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="muted text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</div>
      <div className="bold" style={{ fontSize: 18, marginTop: 4 }}>{value}</div>
      {sub && <div className="muted text-xs">{sub}</div>}
    </div>
  );
}

export default ClinicDetails;
